// ============================================================
// Agency portals + scheduled reports — response types and pure helpers.
//
// Shapes mirror packages/api/src/routes/v2/{portals,portal-public,
// scheduled-reports}.ts and packages/shared/src/analytics/portal.ts.
// Kept snake_case like the rest of the v2 types. Side-effect free.
// ============================================================

// ---------- portals (admin) ----------

/** Reports a portal may expose (mirrors PORTAL_REPORTS in shared). */
export const PORTAL_REPORTS = [
    'overview',
    'timeseries',
    'breakdown',
    'events',
    'ecommerce',
    'goals',
    'funnel',
    'realtime',
    'retention',
    'performance',
] as const
export type PortalReport = (typeof PORTAL_REPORTS)[number]
export const DEFAULT_PORTAL_REPORTS: PortalReport[] = ['overview', 'timeseries', 'breakdown']

export const PORTAL_REPORT_LABELS: Record<PortalReport, string> = {
    overview: 'Overview KPIs',
    timeseries: 'Traffic chart',
    breakdown: 'Breakdowns (channels, pages, countries, devices)',
    events: 'Events',
    ecommerce: 'Ecommerce',
    goals: 'Goals',
    funnel: 'Funnels (API / embeds only)',
    realtime: 'Realtime',
    retention: 'Retention',
    performance: 'Web vitals',
}

export interface PortalBranding {
    productName: string
    logoUrl: string | null
    primaryColor: string
    accentColor: string
    supportEmail: string | null
    hidePoweredBy: boolean
}

export const DEFAULT_PORTAL_BRANDING: PortalBranding = {
    productName: 'Analytics',
    logoUrl: null,
    primaryColor: '#4f46e5',
    accentColor: '#0ea5e9',
    supportEmail: null,
    hidePoweredBy: false,
}

export interface PortalDomain {
    hostname: string
    /** manual | pending | active | moved | blocked | … */
    status: string | null
    records?: Array<{ type: string; name: string; value: string }>
    errors?: string[]
    sslStatus?: string | null
}

export interface Portal {
    id: string
    slug: string
    branding: PortalBranding
    /** Effective: free plans always show it. */
    powered_by: boolean
    reports: string[]
    status: 'active' | 'disabled' | string
    url: string | null
    custom_domain: PortalDomain | null
    created_at: string
    updated_at: string
}

export interface PortalUser {
    id: string
    email: string
    name: string
    role: 'client_viewer'
    sub_tenants: string[]
    status: 'invited' | 'active' | 'disabled' | string
    last_login_at: string | null
    created_at: string
}

export interface InviteResult {
    invite_url: string
    emailed: boolean
}

export type PortalUserCreated = PortalUser &
    InviteResult & {
        unregistered_sub_tenants: string[]
    }

// ---------- scheduled reports ----------

export type DigestFrequency = 'weekly' | 'monthly'

export interface ScheduledReport {
    id: string
    name: string
    frequency: DigestFrequency
    portal_id: string | null
    sub_tenant: string | null
    recipients: string[]
    include_csv: boolean
    active: boolean
    next_run_at: string | null
    last_run_at: string | null
    last_status: string | null
    last_error: string | null
    created_at: string
}

export interface ScheduledReportInput {
    name: string
    frequency: DigestFrequency
    portal_id: string | null
    sub_tenant: string | null
    recipients: string[]
    include_csv: boolean
    active: boolean
}

export interface DigestRunResult {
    status: 'sent' | 'skipped' | 'failed' | 'preview'
    sent: number
    failed: number
    skipped: number
    error?: string
    period: { from: string; to: string }
    preview?: {
        subject: string
        html: string
        text: string
        csv: string | null
        data: unknown
    }
}

// ---------- public portal ----------

export interface PortalPublic {
    id: string
    slug: string
    project_id: string
    branding: PortalBranding
    powered_by: boolean
    reports: string[]
    url: string | null
    timezone: string
    currency: string
}

export interface PortalSessionResponse {
    token: string
    user: { id: string; email: string; name: string }
    portal: PortalPublic
}

export interface PortalMe {
    user: { id: string; email: string; name: string }
    portal: PortalPublic
    clients: Array<{ external_id: string; name: string }>
}

// ============================================================
// Pure helpers
// ============================================================

export const PORTAL_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,46}[a-z0-9])$/

export function isValidPortalSlug(slug: string): boolean {
    return PORTAL_SLUG_RE.test(slug)
}

/** "Example Agency — Reports!" → "example-agency-reports" (max 48 chars). */
export function slugify(name: string): string {
    return name
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48)
        .replace(/-+$/g, '')
}

export const PASSWORD_MIN = 8
export const PASSWORD_MAX = 128

/** Validation message for a new portal password + its confirmation, or null when fine. */
export function passwordProblem(password: string, confirm: string): string | null {
    if (password.length < PASSWORD_MIN) {
        return `Use at least ${PASSWORD_MIN} characters`
    }
    if (password.length > PASSWORD_MAX) {
        return `Use at most ${PASSWORD_MAX} characters`
    }
    if (password !== confirm) {
        return "The passwords don't match"
    }
    return null
}

/** Logo URLs must be https (the API rejects anything else). */
export function isHttpsUrl(v: string): boolean {
    try {
        return new URL(v).protocol === 'https:'
    } catch {
        return false
    }
}

const EMAIL_RE = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/

export function isEmail(v: string): boolean {
    return v.length <= 254 && EMAIL_RE.test(v)
}

/** "a@x.com, b@y.com\nbad" → { emails: [a@x.com, b@y.com], invalid: ['bad'] } (lowercased, deduped). */
export function parseEmailList(raw: string): { emails: string[]; invalid: string[] } {
    const emails: string[] = []
    const invalid: string[] = []
    for (const part of raw.split(/[\s,;]+/)) {
        const v = part.trim().toLowerCase()
        if (!v) {
            continue
        }
        if (!isEmail(v)) {
            invalid.push(v)
        } else if (!emails.includes(v)) {
            emails.push(v)
        }
    }
    return { emails, invalid }
}

/**
 * Reports a portal shows, in display order. Unknown names are dropped, and `funnel`
 * is never rendered (the portal can't list funnel ids); it still gates API/embeds.
 */
export const PORTAL_VIEW_ORDER = [
    'overview',
    'timeseries',
    'realtime',
    'breakdown',
    'events',
    'ecommerce',
    'goals',
    'retention',
    'performance',
] as const
export type PortalSection = (typeof PORTAL_VIEW_ORDER)[number]

export function visiblePortalSections(reports: readonly string[]): PortalSection[] {
    const set = new Set(reports)
    return PORTAL_VIEW_ORDER.filter(r => set.has(r))
}

/** Toggle a report in a portal's list, never leaving it empty; result in canonical order. */
export function toggleReport(reports: readonly string[], report: PortalReport): PortalReport[] {
    const set = new Set(reports)
    if (set.has(report)) {
        if (set.size === 1) {
            return PORTAL_REPORTS.filter(r => set.has(r))
        }
        set.delete(report)
    } else {
        set.add(report)
    }
    return PORTAL_REPORTS.filter(r => set.has(r))
}

/** The client a portal session reports on: the chosen one when it's allowed, else the first. */
export function pickClient(
    clients: ReadonlyArray<{ external_id: string }>,
    chosen: string | null,
): string | null {
    if (chosen && clients.some(c => c.external_id === chosen)) {
        return chosen
    }
    return clients[0]?.external_id ?? null
}

// ---------- API errors ----------

export type ApiErrorKind = 'plan_limit' | 'forbidden' | 'not_configured' | 'other'

/** Classify an RTK Query error from the admin routes (402 plan limit, 403 role, 503 email off). */
export function apiErrorKind(err: unknown): ApiErrorKind | null {
    if (!err) {
        return null
    }
    const e = err as { status?: unknown; data?: { error?: { code?: string } } }
    const code = e.data?.error?.code
    if (e.status === 402 || code === 'PLAN_LIMIT') {
        return 'plan_limit'
    }
    if (e.status === 403) {
        return 'forbidden'
    }
    if (e.status === 503 || code === 'NOT_CONFIGURED') {
        return 'not_configured'
    }
    return 'other'
}

// ---------- custom domain ----------

export type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info'

/** Chip color for a custom-domain (or SSL) status. */
export function domainStatusColor(status: string | null | undefined): ChipColor {
    switch (status) {
        case 'active':
            return 'success'
        case 'pending':
        case 'pending_validation':
        case 'pending_deployment':
        case 'initializing':
            return 'warning'
        case 'manual':
            return 'info'
        case 'blocked':
        case 'moved':
        case 'deleted':
        case 'expired':
            return 'error'
        default:
            return 'default'
    }
}

// ---------- scheduled reports ----------

export function lastRunColor(status: string | null): ChipColor {
    switch (status) {
        case 'sent':
            return 'success'
        case 'partial':
        case 'skipped':
            return 'warning'
        case 'failed':
            return 'error'
        default:
            return 'default'
    }
}

/** Download name for a digest CSV: "<report-name>-<from>_<to>.csv". */
export function digestCsvFilename(name: string, period: { from: string; to: string }): string {
    const base = slugify(name) || 'report'
    return `${base}-${period.from}_${period.to}.csv`
}

/** Notification for a "send now" result. */
export function sendResultMessage(res: DigestRunResult): {
    message: string
    severity: 'success' | 'info' | 'error'
} {
    if (res.status === 'sent') {
        const plural = res.sent === 1 ? '' : 's'
        const skipped = res.skipped ? ` (${res.skipped} unsubscribed)` : ''
        return { message: `Sent to ${res.sent} recipient${plural}${skipped}.`, severity: 'success' }
    }
    if (res.status === 'skipped') {
        return {
            message: `Nothing sent: ${res.error ?? 'every recipient has unsubscribed'}.`,
            severity: 'info',
        }
    }
    return {
        message: `Sending failed${res.error ? `: ${res.error}` : ''} (${res.sent} sent, ${res.failed} failed).`,
        severity: 'error',
    }
}
